<template>
  <v-navigation-drawer
    :value="value"
    app
    right
    temporary
    hide-overlay
    stateless
    width="320"
    class="user-performance-panel"
    @input="$emit('input', $event)"
  >
    <div class="d-flex flex-column h-full">
      <div class="user-performance-panel__header d-flex align-center px-3 py-2">
        <v-btn
          fab
          small
          icon
          :ripple="false"
          @click="$emit('input', false)"
        >
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>

        <div class="ml-2">
          <div class="text-subtitle-1 font-weight-bold">
            {{ userName }}
          </div>
          <div class="text-caption secondary--text">
            {{ date }}
          </div>
        </div>
      </div>

      <!-- 明細・成績(日払いサイン/成績印刷の統合画面)への導線。クロージング系画面からpropで有効化する -->
      <div
        v-if="showStatement"
        class="d-flex align-center px-3 pb-2"
      >
        <v-btn
          outlined
          small
          color="primary"
          @click="$emit('open-statement')"
        >
          <v-icon
            small
            left
          >
            {{ icons.mdiSignatureFreehand }}
          </v-icon>
          明細・成績
        </v-btn>
      </div>

      <!-- v-tabs は自身が flex: 1 1 auto を持ち、flexカラム内で伸びて空白を作るため flex-grow-0 で抑止する -->
      <v-tabs
        v-model="activeTab"
        grow
        height="40"
        class="flex-grow-0"
      >
        <v-tab>{{ tabLabels.performance }}</v-tab>
        <v-tab>{{ tabLabels.customFields }}</v-tab>
      </v-tabs>

      <v-divider />

      <v-tabs-items
        v-model="activeTab"
        class="user-performance-panel__body flex-grow-1"
      >
        <v-tab-item>
          <div class="pa-4">
            <v-skeleton-loader
              v-if="isLoading"
              type="article, table"
            />

            <template v-else>
              <WorkTimeEditor
                :date="date"
                :user-id="userId"
                :working-minutes="workingMinutes"
                @saved="$emit('timecard-saved')"
              />

              <div class="d-flex mb-6">
                <div class="performance-summary">
                  <div class="performance-summary__label">
                    受領数量 合計
                  </div>
                  <div class="performance-summary__value">
                    {{ formatCount(totalCount) }}
                  </div>
                </div>
                <div class="performance-summary">
                  <div class="performance-summary__label">
                    販売価格 合計
                  </div>
                  <div class="performance-summary__value">
                    ¥{{ totalPrice.toLocaleString() }}
                  </div>
                </div>
              </div>

              <div
                v-if="items.length"
                class="performance-section"
              >
                <div class="performance-section__head">
                  <span class="performance-section__swatch" />
                  <span class="performance-section__name">受領商品</span>
                  <span class="performance-section__stat">
                    {{ items.length }}項目
                  </span>
                </div>
                <div class="performance-list">
                  <div
                    v-for="item in items"
                    :key="`item-${item.vendibleType}-${item.vendibleId}`"
                    class="performance-item"
                  >
                    <div class="performance-item__name">
                      {{ item.vendibleName }}
                    </div>
                    <div class="performance-item__stats">
                      <span class="performance-item__count">
                        <span class="performance-item__count-label">数量</span>
                        {{ formatCount(item.count) }}
                      </span>
                      <span class="performance-item__price">
                        ¥{{ Number(item.totalPrice).toLocaleString() }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-else
                class="text-center secondary--text py-8"
              >
                このユーザーの受領商品データはありません
              </div>
            </template>
          </div>
        </v-tab-item>

        <v-tab-item>
          <UserCustomFieldsTab :user-id="userId" />
        </v-tab-item>
      </v-tabs-items>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { ref, onMounted, onUnmounted } from '@vue/composition-api'
import { mdiClose, mdiSignatureFreehand } from '@mdi/js'
import WorkTimeEditor from './WorkTimeEditor.vue'
import UserCustomFieldsTab from './UserCustomFieldsTab.vue'

// タブ表示名（カスタムフィールドタブの名称は未確定のため、確定したらここだけ変更する）
const TAB_LABELS = {
  performance: '内訳',
  customFields: 'カスタム項目',
}

export default {
  name: 'UserPerformancePanel',
  components: {
    WorkTimeEditor,
    UserCustomFieldsTab,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    date: {
      type: String,
      default: '',
    },
    userId: {
      type: Number,
      default: null,
    },
    userName: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default: () => [],
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    totalPrice: {
      type: Number,
      default: 0,
    },
    workingMinutes: {
      type: Number,
      default: 0,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    // 明細・成績(日払いサイン/成績印刷)ボタンの表示可否。クロージング系画面からtrueを渡す
    showStatement: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const activeTab = ref(0)

    // 数量は按分で小数になりうるため、整数なら整数表記、小数なら小数第1位まで「切り捨て」表示する。
    // 例: 0.55 → 0.5、1.83 → 1.8（四捨五入ではなく切り捨て）
    const formatCount = count => {
      const num = Number(count)
      if (Number.isInteger(num)) return num.toLocaleString()

      const truncated = Math.floor(num * 10) / 10

      return truncated.toLocaleString(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })
    }

    const handleKeydown = event => {
      if (!props.value) return

      if (event.key === 'Escape') {
        emit('input', false)
      }
    }

    onMounted(() => {
      window.addEventListener('keydown', handleKeydown)
    })

    onUnmounted(() => {
      window.removeEventListener('keydown', handleKeydown)
    })

    return {
      activeTab,
      formatCount,
      tabLabels: TAB_LABELS,
      icons: {
        mdiClose,
        mdiSignatureFreehand,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.user-performance-panel__body {
  overflow-y: auto;
}

.performance-summary {
  flex: 1;
  padding-right: 16px;

  &:not(:last-child) {
    border-right: 1px solid rgba(150, 150, 150, 0.2);
    margin-right: 16px;
  }

  &__label {
    font-size: 12px;
    color: var(--v-secondary-base, #8a8f9e);
    margin-bottom: 2px;
  }

  &__value {
    font-size: 18px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
}

.performance-section {
  margin-bottom: 24px;

  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__swatch {
    width: 6px;
    height: 18px;
    border-radius: 2px;
    flex-shrink: 0;
    background: #16b6e0;
  }

  &__name {
    font-size: 14px;
    font-weight: 700;
  }

  &__stat {
    margin-left: auto;
    font-size: 12px;
    color: var(--v-secondary-base, #8a8f9e);

    strong {
      font-weight: 700;
    }
  }
}

.performance-list {
  display: flex;
  flex-direction: column;
}

.performance-item {
  padding: 12px 4px;
  border-bottom: 1px dashed rgba(150, 150, 150, 0.18);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(150, 150, 150, 0.06);
  }

  &__name {
    font-size: 13px;
    font-weight: 500;
    word-break: break-word;
    line-height: 1.4;
    margin-bottom: 6px;
  }

  &__stats {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-variant-numeric: tabular-nums;
  }

  &__count {
    font-size: 12px;
    color: var(--v-secondary-base, #8a8f9e);
  }

  &__count-label {
    margin-right: 4px;
    font-size: 11px;
  }

  &__price {
    font-size: 13px;
    font-weight: 600;
  }
}
</style>
